import React, {useState, useMemo, useEffect} from 'react'
import _ from 'lodash'
import {Button, ButtonGroup, Input} from 'reactstrap'
import {useSelectedVariantContext} from '../../../context/SelectedVariantContext'
import DesignLibraryModal from './image-library/DesignLibraryModal'
import CreateBackgroundModal from './create-background/CreateBackgroundModal'
import {
    actionUpdateBgImage,
    actionUpdateBgImageType,
    actionUpdateLoadingBackground,
    actionUpdateListBbImages,
} from '../../../context/SelectedVariantContext/action'
import {fileToBase64, getImageDimensions} from '../../../../../helpers/imageFormat'
import randomstring from 'randomstring'
import {ReactComponent as PlusIcon} from '../../../../../assets/icons/plus.svg'
import {ReactComponent as WarningIcon} from '../../../../../assets/icons/warning_amber.svg'
import {ReactComponent as CreativityIcon} from '../../../../../assets/icons/icon-creativity.svg'
import {ReactComponent as CreateBackgroundIcon} from '../../../../../assets/icons/icon-create-background.svg'
import {getInfomationBackground} from '../../../../../helpers/createProductCombine'
import {handleUploadImageByLink} from '../../../context/SelectedVariantContext/helper'
import {getLocalData, setLocalData} from '../../../../../services/StorageServices'
import {uploadDesignLibrary} from '../../../../../services/ArtworkTemplateServices'
import {isBackofficeEnv} from '../../../../../helpers/isBackoffceEnv'

const UploadBackgroundImage = () => {
    const {
        state: {
            listBackgroundImages,
            currentBgImage,
            currentBgImageType,
            product,
            previewImages,
            previewSrc: previewSrcFromContext,
            reloadPreview,
            previewImagesLoaded,
        },
        dispatch,
    } = useSelectedVariantContext()
   
    const {mockups, variants} = product
    let mockupInfos = _.get(mockups[0], 'meta.mockup_infos', [])

    const previewSrc = previewSrcFromContext || []
    const firstPreviewImage = Array.isArray(previewSrc[0]) ? previewSrc[0][0] : previewSrc[0] || null
    
    const [wPreview, setWPreview] = useState(200)
    const [hPreview, setHPreview] = useState(200)
    const [firstImageLoaded, setFirstImageLoaded] = useState(false)
    const options = [
        {value: 'original', label: 'Original'},
        {value: 'fit', label: 'Fit and center'},
        {value: 'fill', label: 'Scale to Fill'},
    ]
    const [isOpenImageDesign, setIsOpenImageDesign] = useState(false)
    const [isOpenCreateBackground, setIsOpenCreateBackground] = useState(false)
    const _toggle = () => {
        setIsOpenImageDesign((prev) => !prev)
    }
    const _toggleCreateBackground = () => {
        setIsOpenCreateBackground((prev) => !prev)
    }

    // Track when the first preview image is loaded and displayed
    // Verify the first image can be loaded
    useEffect(() => {
        if (!firstPreviewImage) {
            setFirstImageLoaded(false)
            return
        }

        // Reset when preview changes
        setFirstImageLoaded(false)

        const img = new Image()
        let isMounted = true

        img.onload = () => {
            // Verify image is actually loaded and has dimensions
            if (isMounted && img.complete && img.naturalWidth > 0 && img.naturalHeight > 0) {
                setFirstImageLoaded(true)
            }
        }
        img.onerror = () => {
            if (isMounted) {
                setFirstImageLoaded(false)
            }
        }
        img.src = firstPreviewImage

        return () => {
            isMounted = false
            img.onload = null
            img.onerror = null
        }
    }, [firstPreviewImage])

    const handleAddGeneratedBackgrounds = async (selectedItems) => {
        if (!selectedItems || selectedItems.length === 0) return

        try {
            dispatch(actionUpdateLoadingBackground(true))

            // Process each selected image URL
            const newBackgroundImages = await Promise.all(
                selectedItems.map(async (item) => {
                    try {
                        // Fetch the image as a blob
                        const response = await fetch(item.url)
                        const blob = await response.blob()
                        
                        // Extract filename from URL or use default
                        const urlParts = item.url.split('/')
                        const filename = urlParts[urlParts.length - 1].split('?')[0] || 'background-image.jpg'
                        
                        // Create a File object from the blob
                        const file = new File([blob], filename, { type: blob.type })
                        
                        // Convert to base64
                        const fileBase64 = await fileToBase64(file)
                        
                        // Get image dimensions
                        const dimensions = await getImageDimensions(fileBase64)
                        
                        // Generate unique key
                        const key = randomstring.generate(7)
                        
                        return {
                            file: fileBase64,
                            key,
                            naturalWidth: dimensions.w,
                            naturalHeight: dimensions.h,
                        }
                    } catch (error) {
                        console.error('Error processing image:', item.url, error)
                        return null
                    }
                })
            )

            // Filter out any failed conversions
            const validImages = newBackgroundImages.filter(img => img !== null)

            if (validImages.length > 0) {
                // Add to listBackgroundImages
                await dispatch(actionUpdateListBbImages([...listBackgroundImages, ...validImages]))

                // Upload to design library if in backoffice environment
                if (isBackofficeEnv()) {
                    try {
                        await Promise.all(
                            selectedItems.map(async (item) => {
                                try {
                                    const key = item.key || randomstring.generate(7)
                                    const payload = {
                                        name: key,
                                        link: item.url,
                                        is_ai_background: true
                                    }
                                    await uploadDesignLibrary(payload)
                                } catch (error) {
                                    console.error('Error uploading to design library:', item.url, error)
                                }
                            })
                        )
                    } catch (error) {
                        console.error('Error uploading backgrounds to design library:', error)
                    }
                } else {
                    // Add to designLibrary in localData with is_ai_background flag
                    try {
                        const designLibrary = getLocalData('designLibrary') || []
                        const designLibraryItems = await Promise.all(
                            selectedItems.map(async (item) => {
                                try {
                                    // Create imageObj similar to handleUploadImageByLink
                                    const imageObj = await handleUploadImageByLink(item.url, `ai-background-${item.id || randomstring.generate(7)}.jpg`)
                                    // Add is_ai_background field
                                    return {
                                        ...imageObj,
                                        is_ai_background: true
                                    }
                                } catch (error) {
                                    console.error('Error creating designLibrary item:', item.url, error)
                                    return null
                                }
                            })
                        )
                        
                        // Filter out failed items and add to designLibrary
                        const validDesignLibraryItems = designLibraryItems.filter(item => item !== null)
                        if (validDesignLibraryItems.length > 0) {
                            designLibrary.push(...validDesignLibraryItems)
                            setLocalData('designLibrary', designLibrary)
                        }
                    } catch (error) {
                        console.error('Error adding to designLibrary:', error)
                    }
                }

                // Optionally select the first added image
                if (validImages.length > 0) {
                    const firstImage = validImages[0]
                    const info = getInfomationBackground(currentBgImageType, {
                        naturalWidth: firstImage.naturalWidth,
                        naturalHeight: firstImage.naturalHeight,
                    })
                    const {width, height, x, y} = info
                    const backgroundImage = {
                        naturalWidth: firstImage.naturalWidth,
                        naturalHeight: firstImage.naturalHeight,
                        id: `backgroundImage`,
                        file: firstImage.file,
                        key: firstImage.key,
                        width,
                        height,
                        x,
                        y,
                    }
                    dispatch(actionUpdateBgImage(backgroundImage))
                }
            }

            dispatch(actionUpdateLoadingBackground(false))
        } catch (error) {
            console.error('Error adding generated backgrounds:', error)
            dispatch(actionUpdateLoadingBackground(false))
        }
    }
    const onChangeBackgroundType = (value) => {
        const info = getInfomationBackground(value, currentBgImage)
        const {width, height, x, y} = info
        const backgroundImage = {
            ...currentBgImage,
            width,
            height,
            x,
            y,
        }
        dispatch(actionUpdateBgImage(backgroundImage))
        dispatch(actionUpdateBgImageType(value))
    }

    const onSelectBackgroundImage = (image = null) => {
        if (!!image) {
            const info = getInfomationBackground(currentBgImageType, image)
            const {width, height, x, y} = info
            const newBackgroundImage = {
                ...currentBgImage,
                ...image,
                width,
                height,
                x,
                y,
            }
            console.log(33333, width, height, x, y, newBackgroundImage)
            if (currentBgImage.key !== image.key) dispatch(actionUpdateLoadingBackground(true))
            dispatch(actionUpdateBgImage(newBackgroundImage))
        } else {
            dispatch(actionUpdateLoadingBackground(true))
            dispatch(actionUpdateBgImage({}))
        }
    }

    // const getInfoPreviewArtworkSlider = () => {
    //     let w = 0
    //     let h = 0
    //     if (document.querySelector('.PreviewArtworkContainer')) {
    //         w = document.querySelector('.PreviewArtworkContainer').offsetWidth
    //         h = document.querySelector('.PreviewArtworkContainer').offsetHeight
    //     }
    //     setWPreview(w)
    //     setHPreview(h)
    // }

    // useEffect(() => {
    //     getInfoPreviewArtworkSlider()
    //     window.addEventListener('resize', getInfoPreviewArtworkSlider)
    //     // eslint-disable-next-line react-hooks/exhaustive-deps
    // }, [])

    // useEffect(async () => {
    //     //getInfoPreviewArtworkSlider()
    //     //window.addEventListener('resize', getInfoPreviewArtworkSlider)
    // }, [])

    return (
        <div className="UploadBackgroundImage">
            <h4 className="TitleVariantView">Backgrounds</h4>
            {currentBgImage && currentBgImage?.naturalHeight < 2000 && currentBgImage?.naturalWidth < 2000 && (
                <p className="alert-warning">
                    <WarningIcon /> Images with a resolution lower than 2000 x 2000 pixels may appear blurry, which
                    could also impact the ranking of your listing
                </p>
            )}
            <div className="listBackgroundImages">
                <div
                    id="upload_mockup_background"
                    className="itemUploadBackgroundImage selectImage flex-column"
                    onClick={_toggle}
                >
                    <div className="mb-1">
                        <PlusIcon />
                    </div>
                    Upload
                </div>
                <div
                    className={
                        currentBgImage.key === undefined
                            ? 'itemUploadBackgroundImage active'
                            : 'itemUploadBackgroundImage'
                    }
                    onClick={() => onSelectBackgroundImage()}
                >
                    <img src={mockupInfos[0].parts[0].image_path} alt="Mockup background" />
                    <div className="RadioWrapper">
                        <Input
                            type="radio"
                            className="custom-control-input"
                            id="default"
                            checked={currentBgImage.key === undefined}
                            // onChange={handleSelectAllVariant}
                        />
                        <label></label>
                    </div>
                </div>
                {listBackgroundImages.length > 0 &&
                    listBackgroundImages.map((item) => {
                        return (
                            <div
                                className={
                                    currentBgImage.key === item.key
                                        ? 'itemUploadBackgroundImage active'
                                        : 'itemUploadBackgroundImage'
                                }
                                onClick={() => onSelectBackgroundImage(item)}
                            >
                                <img src={item?.file} alt="" />
                                <div className="RadioWrapper">
                                    <Input
                                        type="radio"
                                        className="custom-control-input"
                                        id={item.key}
                                        checked={currentBgImage.key === item.key}
                                        // onChange={handleSelectAllVariant}
                                    />
                                    <label></label>
                                </div>
                            </div>
                        )
                    })}
            </div>
            {currentBgImage.file !== undefined && (
                <div className="GroupButtonType d-flex">
                    <ButtonGroup>
                        {options.map((item) => {
                            return (
                                <Button
                                    id="effect_mockup_background"
                                    className={`${currentBgImageType === item.value ? 'active' : ''}`}
                                    outline
                                    onClick={() => onChangeBackgroundType(item.value)}
                                >
                                    {item.label}
                                </Button>
                            )
                        })}
                    </ButtonGroup>
                </div>
            )}
            <p className="Recommended">
                Recommended resolution 2000 x 2000 px (72 DPI). Supported PNG, JPG, SVG files. Maximum file storage 50MB
            </p>
            <div className="CreateBackgroundCTA">
                <div className="CreateBackgroundBanner">
                    <div className="BannerContent">
                        <CreativityIcon className="CreativityIcon" />
                        <p className="BannerText">
                            Boost your creativity — generate stunning backgrounds with AI!
                        </p>
                    </div>
                    <button
                        id={'create_background_btn'}
                        type={'button'}
                        className={'btn btn-white CreateBackgroundButton'}
                        onClick={_toggleCreateBackground}
                        disabled={!firstPreviewImage || reloadPreview || !firstImageLoaded || !previewImagesLoaded}
                    >
                        <CreateBackgroundIcon className="CreateBackgroundIcon" />
                        Create background
                    </button>
                </div>
            </div>
            {isOpenImageDesign && (
                <DesignLibraryModal
                    isOpen={isOpenImageDesign}
                    toggle={_toggle}
                    wPreview={wPreview}
                    hPreview={hPreview}
                />
            )}
            {isOpenCreateBackground && (
                <CreateBackgroundModal
                    isOpen={isOpenCreateBackground}
                    toggle={_toggleCreateBackground}
                    wPreview={wPreview}
                    hPreview={hPreview}
                    onAddBackground={handleAddGeneratedBackgrounds}
                    defaultImageUrl={firstPreviewImage || null}
                />
            )}
        </div>
    )
}

export default UploadBackgroundImage

